// SimpleEventFeature.js - Version épurée et lisible
import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { eventService } from '../api';
import { useTranslation } from 'react-i18next';

// Container principal simple mais élégant
const EventContainer = styled(motion.div)`
  margin-top: 2rem;
  border: 2px solid ${props => props.theme.primary || '#3498db'};
  border-radius: 10px;
  padding: 1.5rem;
  background: ${props => props.theme.isDark ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)'};
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  max-width: 550px;
  width: 100%;
`;

// En-tête avec titre clairement visible
const EventHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'};
  padding-bottom: 1rem;
`;

// Titre de l'événement en grand
const EventTitle = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${props => props.theme.isDark ? '#fff' : '#333'};
  margin: 0;
  flex: 1;
`;

// Badge de statut simple et lisible
const StatusBadge = styled.div`
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
  background-color: ${props => {
    switch (props.status) {
      case 'upcoming':
        return props.theme.accent || '#f39c12';
      case 'ongoing':
        return props.theme.primary || '#3498db';
      case 'completed':
        return props.theme.success || '#2ecc71';
      case 'cancelled':
        return props.theme.error || '#e74c3c';
      default:
        return '#95a5a6';
    }
  }};
`;

// Informations de l'événement clairement présentées
const EventInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 1rem;
  color: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)'};
  
  i {
    width: 24px;
    margin-right: 0.8rem;
    color: ${props => props.theme.primary || '#3498db'};
    font-size: 1.1rem;
  }
`;

// Boutons d'action bien visibles
const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
`;

// Bouton principal
const PrimaryButton = styled(Link)`
  padding: 0.7rem 1.5rem;
  background-color: ${props => props.theme.primary || '#3498db'};
  color: white;
  border-radius: 5px;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  
  i {
    margin-left: 0.5rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.primaryDark || '#2980b9'};
  }
`;

// Lien secondaire
const SecondaryLink = styled(Link)`
  color: ${props => props.theme.isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)'};
  text-decoration: none;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  
  i {
    margin-left: 0.4rem;
  }
  
  &:hover {
    color: ${props => props.theme.primary || '#3498db'};
  }
`;

// Composant principal
const FeaturedEvent = ({ theme }) => {
  const [latestEvent, setLatestEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  
  useEffect(() => {
    const fetchLatestEvent = async () => {
      try {
        setLoading(true);
        const response = await eventService.getAll({ limit: 1, ordering: '-date' });
        
        let eventData = null;
        if (response.data && Array.isArray(response.data.results) && response.data.results.length > 0) {
          eventData = response.data.results[0];
        } else if (Array.isArray(response.data) && response.data.length > 0) {
          eventData = response.data[0];
        }
        
        setLatestEvent(eventData);
      } catch (err) {
        console.error("Erreur lors du chargement de l'événement récent:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLatestEvent();
  }, []);
  
  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(navigator.language, options);
  };
  
  // Traduction du statut
  const getStatusLabel = (status) => {
    switch (status) {
      case 'upcoming':
        return t('upcoming');
      case 'ongoing':
        return t('ongoing');
      case 'completed':
        return t('completed');
      case 'cancelled':
        return t('cancelled');
      default:
        return status;
    }
  };
  
  if (loading || !latestEvent) {
    return null;
  }
  
  return (
    <EventContainer
      theme={theme}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.9 }}
    >
      <EventHeader theme={theme}>
        <EventTitle theme={theme}>
          {latestEvent.title}
        </EventTitle>
        <StatusBadge theme={theme} status={latestEvent.status}>
          {getStatusLabel(latestEvent.status)}
        </StatusBadge>
      </EventHeader>
      
      <EventInfo theme={theme}>
        <i className="far fa-calendar-alt"></i>
        <span>
          {formatDate(latestEvent.date || latestEvent.start_date)}
          {(latestEvent.end_date && latestEvent.end_date !== (latestEvent.date || latestEvent.start_date)) && 
            ` - ${formatDate(latestEvent.end_date)}`}
        </span>
      </EventInfo>
      
      <EventInfo theme={theme}>
        <i className="far fa-clock"></i>
        <span>{latestEvent.time || '00:00'} {latestEvent.end_time && `- ${latestEvent.end_time}`}</span>
      </EventInfo>
      
      <EventInfo theme={theme}>
        <i className="fas fa-map-marker-alt"></i>
        <span>{latestEvent.is_online ? t('onlineEvent') : (latestEvent.location || t('locationToBeDetermined'))}</span>
      </EventInfo>
      
      <ActionButtons>
        <PrimaryButton 
          to={`/events/${latestEvent.id || latestEvent.id}`} 
          theme={theme}
        >
          {latestEvent.status === 'upcoming' ? t('registerNow') : t('viewDetails')}
          <i className="fas fa-arrow-right"></i>
        </PrimaryButton>
        
        <SecondaryLink 
          to="/events" 
          theme={theme}
        >
          {t('viewAllEvents')}
          <i className="fas fa-chevron-right"></i>
        </SecondaryLink>
      </ActionButtons>
    </EventContainer>
  );
};

export default FeaturedEvent;